import { debounce } from "lodash";
import React, { useCallback, useMemo } from "react";
import HttpService from "src/services/httpService";
import RouteService from "src/services/routeService";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";

import { checkGuidAvailability } from "@admin/api/workspace";
import { unpackError } from "@dashboard/devices/api/helpers";
import { useTheme } from "@mui/material";
import { UncontrolledTextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

interface Props {
    workspace: API.Workspace;
    open: boolean;
    onClose: () => void;
    onSuccess: (updatedWorkspace: API.Workspace) => void;
}

const EditWorkspaceModal = ({ workspace, open, onClose, onSuccess }: Props) => {
    const [guidError, setGuidError] = React.useState<string | undefined>();
    const [nameError, setNameError] = React.useState<string | undefined>();
    const [isValidating, setIsValidating] = React.useState(false);
    const [currentGuid, setCurrentGuid] = React.useState(workspace.guid);
    const [currentName, setCurrentName] = React.useState(workspace.name);
    const [currentOrganization, setCurrentOrganization] = React.useState(workspace.organizationName || "");

    const theme = useTheme();

    const updateWorkspace = async (workspaceData: API.Workspace, newGuid: string, newName: string, newOrganization: string) => {
        try {
            const endpoint = await RouteService.getWorkspacesRoute();
            const payload = {
                name: newName,
                organizationName: newOrganization,
                guid: newGuid,
                organizationVatId: workspaceData.organizationVatId || "",
                defaultGroupId: workspaceData.defaultGroupId,
            };
            const { data } = await HttpService.put<API.Workspace>(`${endpoint}${workspaceData.id}`, payload);
            return data;
        } catch (error) {
            throw unpackError(error);
        }
    };

    const validateGuid = useCallback(
        async (guid: string) => {
            if (!guid.trim() || guid === workspace.guid) {
                setGuidError(undefined);
                setIsValidating(false);
                return;
            }

            setIsValidating(true);
            try {
                const result = await checkGuidAvailability(guid);

                if (result.available) {
                    setGuidError(undefined);
                } else {
                    setGuidError("GUID is already taken");
                }
            } catch (_error) {
                setGuidError("Error checking GUID availability");
            } finally {
                setIsValidating(false);
            }
        },
        [workspace.guid],
    );

    const debouncedValidateGuid = useMemo(() => debounce(validateGuid, 1000), [validateGuid]);

    const handleGuidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCurrentGuid(newValue);
        void debouncedValidateGuid(newValue);

        if (guidError && newValue !== currentGuid) {
            setGuidError(undefined);
        }
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCurrentName(newValue);

        if (!newValue.trim()) {
            setNameError("Name is required");
        } else if (nameError) {
            setNameError(undefined);
        }
    };

    const handleOrganizationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCurrentOrganization(newValue);
    };

    const onSubmit = async () => {
        if (guidError || nameError || !currentGuid.trim() || !currentName.trim()) {
            return;
        }

        try {
            const updatedWorkspace = await updateWorkspace(workspace, currentGuid, currentName, currentOrganization);
            showSuccessToast("Workspace updated successfully");
            onSuccess(updatedWorkspace);
        } catch (error: unknown) {
            const err = error as Error;
            showErrorToast(err.message || "Failed to update workspace");
        }
    };

    const hasError = !!guidError || !!nameError;
    const isPristine =
        currentGuid === workspace.guid &&
        currentName === workspace.name &&
        currentOrganization === (workspace.organizationName || "");
    const isDisabled = isPristine || isValidating || hasError || !currentGuid.trim() || !currentName.trim();

    const getErrorMessage = () => {
        if (guidError) {
            return guidError;
        }
        if (!currentGuid.trim()) {
            return "GUID is required";
        }
        return undefined;
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Edit Workspace"
            endButton={
                <PrimaryButton onClick={onSubmit} disabled={isDisabled}>
                    {isValidating ? "Validating..." : "Save"}
                </PrimaryButton>
            }
        >
            <UncontrolledTextField
                label="Name"
                value={currentName}
                onChange={handleNameChange}
                required
                errorMessage={nameError}
                style={{ marginBottom: theme.spacing(3) }}
            />
            <UncontrolledTextField
                label="GUID"
                value={currentGuid}
                onChange={handleGuidChange}
                required
                errorMessage={getErrorMessage()}
                style={{ marginBottom: theme.spacing(3) }}
            />
            <UncontrolledTextField label="Organization" value={currentOrganization} onChange={handleOrganizationChange} />
        </Modal>
    );
};

export default EditWorkspaceModal;
